import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faAnglesLeft,
    faAnglesRight,
    faCalendarDays,
    faChevronLeft,
    faChevronRight,
    faCompass,
    faCrow,
    faFilter,
    faSignsPost
} from "@fortawesome/free-solid-svg-icons";
import {pluralize} from "../../helpers/helpers";
import {BASE_MONTHS} from "../../helpers/months";
import {ExplorerHero, ExplorerHeroStat, ExplorerHeroStats} from "../../components/explorer-hero";
import {ChecklistSearch} from "../../components/checklist-search";
import {SegmentedFilter} from "../../components/segmented-filter";
import {HOTSPOT_EXPLORER_PAGE_SIZE} from "./constants";
import {getHotspotExplorerHeroImage} from "./helpers";
import type {HotspotExplorerGroup, HotspotExplorerHotspot, HotspotExplorerSortMode, HotspotExplorerStats} from "./types";
import {HotspotTable} from "./hotspot-table";
import {getHotspotExplorerPageSlice} from "./helpers";
import {PRE_EBIRD_LIST_BUILDING_YEAR} from "../../helpers/storedBirdData";
import {PRE_EBIRD_LIST_BUILDING_LABEL} from "../dashboard/constants";
import './hotspot-explorer-shell.scss';

type HotspotExplorerShellProps = {
    eyebrow: string,
    title: string,
    intro: string,
    backgroundImage?: string,
    backLabel?: string,
    onBack?: () => void,
    stats: HotspotExplorerStats,
    childStatLabel?: string,
    showChildStat?: boolean,
    showYearsExploring?: boolean,
    selectedYear?: string,
    selectedMonth?: string,
    yearOptions?: number[],
    onChangePeriod?: (year: string, month: string) => void,
    panelHeading: string,
    groups: HotspotExplorerGroup[],
    activeGroupCode?: string,
    onSelectGroup: (code: string) => void,
    hotspots: HotspotExplorerHotspot[],
    sortMode: HotspotExplorerSortMode,
    onChangeSort: (sortMode: HotspotExplorerSortMode) => void,
    currentPage: number,
    onPageChange: (page: number) => void,
    onOpenHotspot: (locationId: string) => void
}

export function HotspotExplorerShell({
    eyebrow,
    title,
    intro,
    backgroundImage,
    backLabel,
    onBack,
    stats,
    childStatLabel,
    showChildStat,
    showYearsExploring: showYearsExploringProp,
    selectedYear = 'life',
    selectedMonth = 'all',
    yearOptions = [],
    onChangePeriod,
    panelHeading,
    groups,
    activeGroupCode,
    onSelectGroup,
    hotspots,
    sortMode,
    onChangeSort,
    currentPage,
    onPageChange,
    onOpenHotspot
}: HotspotExplorerShellProps) {
    const heroImage = useMemo(() => backgroundImage ?? getHotspotExplorerHeroImage(), [backgroundImage]);
    const filterPanelRef = useRef<HTMLElement | null>(null);
    const childStatVisible = showChildStat ?? true;
    const showYearsExploring = showYearsExploringProp ?? true;
    const [searchTerm, setSearchTerm] = useState('');
    const filteredHotspots = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        if (!normalizedSearch) return hotspots;

        return hotspots.filter(hotspot => {
            return [
                hotspot.location,
                hotspot.countyLabel,
                hotspot.stateLabel,
                hotspot.countryLabel,
                hotspot.visits.toString(),
                hotspot.speciesSeen.toString(),
                hotspot.lastVisited
            ].some(value => value.toLowerCase().includes(normalizedSearch));
        });
    }, [hotspots, searchTerm]);
    const hotspotPage = useMemo(
        () => getHotspotExplorerPageSlice(filteredHotspots, currentPage, HOTSPOT_EXPLORER_PAGE_SIZE),
        [currentPage, filteredHotspots]
    );

    useEffect(() => {
        if (hotspotPage.currentPage !== currentPage) {
            onPageChange(hotspotPage.currentPage);
        }
    }, [currentPage, hotspotPage.currentPage, onPageChange]);

    const startItem = filteredHotspots.length ? hotspotPage.startIndex + 1 : 0;
    const endItem = hotspotPage.endIndex;
    const totalPages = hotspotPage.totalPages;
    const showPaginationControls = totalPages > 1;
    const selectedYearIsListBuilding = Number(selectedYear) === PRE_EBIRD_LIST_BUILDING_YEAR;
    const showMonthControl = !selectedYearIsListBuilding;
    const scrollToFilterPanel = () => {
        filterPanelRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    };
    const handlePageChange = (page: number) => {
        if (page === hotspotPage.currentPage) return;

        onPageChange(page);
        window.requestAnimationFrame(() => {
            window.scrollTo({top: 0, behavior: 'smooth'});
        });
    };

    return (
        <section className="hotspot-explorer">
            <ExplorerHero
                className="explorer-hero--hotspot"
                backgroundImage={heroImage}
                backLabel={backLabel}
                onBack={onBack}
                eyebrow={eyebrow}
                title={title}
                intro={intro}
            >
                <ExplorerHeroStats ariaLabel="Hotspot summary">
                    {childStatVisible ? (
                        <ExplorerHeroStat
                            icon={<FontAwesomeIcon icon={faSignsPost} />}
                            label={childStatLabel ?? stats.childLabel}
                            value={stats.childCount.toLocaleString()}
                        />
                    ) : null}
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faCompass} />}
                        label={pluralize(hotspots.length, 'Hotspot Visited', 'Hotspots Visited')}
                        value={hotspots.length.toLocaleString()}
                    />
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faCrow} />}
                        label="Species Seen"
                        value={stats.speciesSeen.toLocaleString()}
                    />
                    {showYearsExploring ?
                        <ExplorerHeroStat
                            icon={<FontAwesomeIcon icon={faCalendarDays} />}
                            label={pluralize(stats.yearsExploring, 'Different Year', 'Different Years')}
                            value={stats.yearsExploring.toLocaleString()}
                        />
                        : null
                    }
                </ExplorerHeroStats>
            </ExplorerHero>

            <div className="hotspot-explorer__content">
                <div className="hotspot-explorer__list-panel">
                    <div className="hotspot-explorer__list-panel-header">
                        <div className="hotspot-explorer__list-toolbar">
                            <ChecklistSearch
                                label="Search hotspots"
                                value={searchTerm}
                                placeholder="Search hotspots..."
                                onChange={setSearchTerm}
                            />
                            <button
                                type="button"
                                className="hotspot-explorer__filter-cta"
                                onClick={scrollToFilterPanel}
                            >
                                <FontAwesomeIcon icon={faFilter} aria-hidden="true" />
                                Filter
                            </button>
                            <div className="hotspot-explorer__sort-scroll">
                                <SegmentedFilter
                                    activeId={sortMode}
                                    ariaLabel="Hotspot sort options"
                                    className="hotspot-explorer__sort-tabs"
                                    compact
                                    onChange={onChangeSort}
                                    options={[
                                        {id: 'visits', label: 'Most Visited'},
                                        {id: 'species', label: 'Most Species'},
                                        {id: 'recent', label: 'Most Recent'}
                                    ]}
                                />
                            </div>
                        </div>
                        <div className="hotspot-explorer__result-row">
                            <span>
                                {filteredHotspots.length
                                    ? `Showing ${startItem.toLocaleString()}-${endItem.toLocaleString()} of ${filteredHotspots.length.toLocaleString()} hotspots`
                                    : 'No hotspots match your search'}
                            </span>
                            <div className="hotspot-explorer__period-controls" aria-label="Hotspot period filter">
                                <label className="hotspot-explorer__period-control">
                                    <span>Year</span>
                                    <select
                                        value={selectedYear}
                                        onChange={event => {
                                            const nextYear = event.target.value;
                                            onChangePeriod?.(
                                                nextYear,
                                                Number(nextYear) === PRE_EBIRD_LIST_BUILDING_YEAR ? 'all' : selectedMonth
                                            );
                                        }}
                                    >
                                        <option value="life">All Years</option>
                                        {yearOptions.map(year => (
                                            <option key={year} value={year}>
                                                {year === PRE_EBIRD_LIST_BUILDING_YEAR ? PRE_EBIRD_LIST_BUILDING_LABEL : year}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {showMonthControl ? (
                                    <label className="hotspot-explorer__period-control">
                                        <span>Month</span>
                                        <select
                                            value={selectedMonth}
                                            onChange={event => onChangePeriod?.(selectedYear, event.target.value)}
                                        >
                                            <option value="all">All Months</option>
                                            {BASE_MONTHS.map(month => (
                                                <option key={month.value} value={month.value}>{month.label}</option>
                                            ))}
                                        </select>
                                    </label>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <HotspotTable hotspots={hotspotPage.items} onOpenHotspot={onOpenHotspot} />
                    <div className="hotspot-explorer__pagination" aria-label="Hotspot pagination">
                        <div className="hotspot-explorer__pagination-summary">
                            <span>
                                Page {hotspotPage.currentPage.toLocaleString()} of {totalPages.toLocaleString()}
                            </span>
                            <small>{filteredHotspots.length.toLocaleString()} hotspots total</small>
                        </div>
                        {showPaginationControls ? (
                            <nav className="hotspot-explorer__pagination-controls" aria-label="Page navigation">
                                <button
                                    type="button"
                                    className="hotspot-explorer__pagination-button"
                                    onClick={() => handlePageChange(1)}
                                    disabled={hotspotPage.currentPage === 1}
                                >
                                    <FontAwesomeIcon icon={faAnglesLeft} aria-hidden="true" />
                                    <span>First</span>
                                </button>
                                <button
                                    type="button"
                                    className="hotspot-explorer__pagination-button"
                                    onClick={() => handlePageChange(hotspotPage.currentPage - 1)}
                                    disabled={hotspotPage.currentPage === 1}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                                    <span>Prev</span>
                                </button>
                                <button
                                    type="button"
                                    className="hotspot-explorer__pagination-button"
                                    onClick={() => handlePageChange(hotspotPage.currentPage + 1)}
                                    disabled={hotspotPage.currentPage === totalPages}
                                >
                                    <span>Next</span>
                                    <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    className="hotspot-explorer__pagination-button"
                                    onClick={() => handlePageChange(totalPages)}
                                    disabled={hotspotPage.currentPage === totalPages}
                                >
                                    <span>Last</span>
                                    <FontAwesomeIcon icon={faAnglesRight} aria-hidden="true" />
                                </button>
                            </nav>
                        ) : null}
                    </div>
                </div>

                <aside className="hotspot-explorer__panel" aria-label="Explore Your Places" ref={filterPanelRef}>
                    {groups.length ? (
                        <section className="hotspot-explorer__panel-section">
                            <h3 className="hotspot-explorer__panel-heading">{panelHeading}</h3>
                            <ul className="hotspot-explorer__group-list">
                                {groups.map(group => (
                                    <li key={group.code}>
                                        <button
                                            type="button"
                                            className={`hotspot-explorer__group-button ${activeGroupCode === group.code ? 'is-active' : ''}`}
                                            onClick={() => onSelectGroup(group.code)}
                                        >
                                            <span>{group.label}</span>
                                            <strong>{group.count.toLocaleString()}</strong>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ) : (
                        <div className="hotspot-explorer__panel-empty">
                            <strong>No further subdivisions available</strong>
                            <span>This place only has hotspot-level data, so there is nothing else to drill into here.</span>
                        </div>
                    )}
                </aside>
            </div>
        </section>
    );
}
